import {
  FieldOption,
  FilterInput,
  LogicalOperator,
  OperatorOption,
} from '@/components/publicSearch/constants';
import { normalizeLegacyPercentileFilters } from '@/components/publicSearch/search/ebvPercentileFilterHelpers';
import { tryExpandFilterInputToComparisonRows } from '@/components/publicSearch/search/searchFilterEbvExpansion';
import {
  isIdentChipQuickSearchField,
  isIdentChipStyleFilter,
} from '@/components/publicSearch/search/animalIdentChipFilter';
import {
  IdentChipGqlField,
  processIdentChipFilterToGql,
  type GqlCompositeBranchResolver,
  type GqlFilterSubGroup,
} from '@/components/publicSearch/search/identChipGql';
import {
  ANIMAL_LOOKUP_FIELD_PREFIX,
  getAnimalLookupResolverFromSearchSettings,
} from '@/components/publicSearch/search/constants';
import { expandI6LookupMarkerFilters } from '@/components/publicSearch/search/expandI6LookupFilters';
import type {
  ComparisonOperator,
  FilterGroupOfLotAnimalEbvFilteredFilterOptionInput,
  LotAnimalEbvFilterField,
  LotAnimalEbvFilteredFilterOptionInput,
} from '@/graphqlClient/types';

type SettingsArray =
  | Array<{ societySettingType?: string | null; value?: string | null }>
  | null
  | undefined;

const LOT_ANIMAL_GQL_FIELD = {
  animalTattoo: 'ANIMAL_TATTOO',
} as const;

function mapLotIdentChipSubGroup(
  sg: GqlFilterSubGroup,
): FilterGroupOfLotAnimalEbvFilteredFilterOptionInput {
  return {
    filters: sg.filters.map((f) => ({
      field: f.field as LotAnimalEbvFilterField,
      operator: f.operator as ComparisonOperator,
      value: f.value,
    })),
    groups: sg.groups?.map(mapLotIdentChipSubGroup),
    logicalOperator: sg.logicalOperator as LogicalOperator,
  };
}

function transformLotAnimalField(field: string) {
  if (field === FieldOption.searchTerm.value) return IdentChipGqlField.animal;
  if (field === FieldOption.breeder.value) return IdentChipGqlField.breeder;
  if (field === FieldOption.currentOwner.value) return IdentChipGqlField.owner;
  if (field === FieldOption.dam.value) return IdentChipGqlField.dam;
  if (field === FieldOption.sire.value) return IdentChipGqlField.sire;
  if (field === FieldOption.tattoo.value)
    return LOT_ANIMAL_GQL_FIELD.animalTattoo;

  // Society custom fields (e.g. SocietyAnimalField5) → SOCIETY_ANIMAL_FIELD5
  const societyAnimalFieldMatch = field.match(/^SocietyAnimalField(\d+)$/i);
  if (societyAnimalFieldMatch) {
    return `SOCIETY_ANIMAL_FIELD${societyAnimalFieldMatch[1]}`;
  }

  // i6LookupString rows sometimes use SOCIETY_ANIMAL_FIELD_5 (underscore).
  const societyEnumUnderscore = field.match(/^SOCIETY_ANIMAL_FIELD_(\d+)$/i);
  if (societyEnumUnderscore) {
    return `SOCIETY_ANIMAL_FIELD${societyEnumUnderscore[1]}`;
  }

  const percentileMatch = field.match(/^ebv(\d+)Percentile$/i);
  if (percentileMatch) {
    const number = percentileMatch[1];
    return `EBV${number}_PERCENTILE`;
  }

  const ebvMatch = field.match(/^ebv(\d+)(Acc)?$/i);
  if (ebvMatch) {
    const number = ebvMatch[1];
    const isAcc = ebvMatch[2] !== undefined;
    return `EBV${number}${isAcc ? '_ACCURACY' : ''}`;
  }

  return field;
}

const resolveLotAnimalChipBranchFields: GqlCompositeBranchResolver = (
  field,
) => {
  switch (field) {
    case IdentChipGqlField.animal:
      return [IdentChipGqlField.animalIdent, IdentChipGqlField.animalName];
    case IdentChipGqlField.sire:
    case IdentChipGqlField.dam:
    case IdentChipGqlField.breeder:
    case IdentChipGqlField.owner:
      // LotAnimalEbvFilterField exposes these composite fields, but not the
      // corresponding *_IDENT fields used by the main animal query.
      return [field];
    default:
      return null;
  }
};

function lookupGroupsToLotSubGroups(
  groups: FilterInput[][],
  logicalOperator: LogicalOperator,
): FilterGroupOfLotAnimalEbvFilteredFilterOptionInput[] {
  return groups.map((group) => ({
    filters: group.map((f) => ({
      field: transformLotAnimalField(f.field) as LotAnimalEbvFilterField,
      operator: f.operator as ComparisonOperator,
      value: f.value,
    })),
    logicalOperator,
  }));
}

function processLotFiltersToGql(filters: FilterInput[]): {
  processedFilters: LotAnimalEbvFilteredFilterOptionInput[];
  chipSubGroups: FilterGroupOfLotAnimalEbvFilteredFilterOptionInput[];
} {
  const processedFilters: LotAnimalEbvFilteredFilterOptionInput[] = [];
  const chipSubGroups: FilterGroupOfLotAnimalEbvFilteredFilterOptionInput[] =
    [];

  const normalizedFilters = normalizeLegacyPercentileFilters(filters);

  for (const { field, operator, value } of normalizedFilters) {
    if (!field) continue;
    // Unexpanded lookup markers must not be sent as GraphQL filter fields.
    if (field.startsWith(ANIMAL_LOOKUP_FIELD_PREFIX)) continue;
    if (operator === 'LOOKUP') continue;

    if (
      isIdentChipQuickSearchField(field) &&
      typeof value === 'string' &&
      isIdentChipStyleFilter(operator, value)
    ) {
      const { processed: chipRows, subGroups: chipGroups } =
        processIdentChipFilterToGql(
          field,
          value,
          transformLotAnimalField,
          resolveLotAnimalChipBranchFields,
        );
      if (chipRows.length === 0 && chipGroups.length === 0) continue;
      for (const row of chipRows) {
        processedFilters.push({
          field: row.field as LotAnimalEbvFilterField,
          operator: row.operator as ComparisonOperator,
          value: row.value,
        });
      }
      for (const sg of chipGroups) {
        chipSubGroups.push(mapLotIdentChipSubGroup(sg));
      }
      continue;
    }

    const expandedRows = tryExpandFilterInputToComparisonRows({
      field,
      operator,
      value,
    });
    if (expandedRows !== null) {
      for (const row of expandedRows) {
        processedFilters.push({
          field: transformLotAnimalField(row.field) as LotAnimalEbvFilterField,
          operator: row.operator as ComparisonOperator,
          value: row.value,
        });
      }
      continue;
    }

    if (operator && (value || operator === OperatorOption.isNotEmpty.value)) {
      processedFilters.push({
        field: transformLotAnimalField(field) as LotAnimalEbvFilterField,
        operator: operator as ComparisonOperator,
        value: typeof value === 'string' ? value : String(value),
      });
    }
  }

  return { processedFilters, chipSubGroups };
}

export function buildLotAnimalFilterGroups({
  saleId,
  lotNo,
  filters,
  commonFilterOperator,
  settings,
}: {
  saleId: string;
  lotNo?: string | null;
  filters: FilterInput[];
  commonFilterOperator: LogicalOperator;
  settings?: SettingsArray;
}): FilterGroupOfLotAnimalEbvFilteredFilterOptionInput[] {
  const baseFilters = [
    {
      field: FieldOption.saleId.value as LotAnimalEbvFilterField,
      operator: OperatorOption.equals.value as ComparisonOperator,
      value: saleId,
    },
  ];

  if (lotNo) {
    baseFilters.push({
      field: FieldOption.lotNo.value as LotAnimalEbvFilterField,
      operator: OperatorOption.equals.value as ComparisonOperator,
      value: lotNo,
    });
  }

  const resolver = getAnimalLookupResolverFromSearchSettings(settings);
  const {
    filters: lookupExpanded,
    andGroups,
    orGroups,
  } = expandI6LookupMarkerFilters(
    filters,
    resolver,
    ANIMAL_LOOKUP_FIELD_PREFIX,
  );
  const { processedFilters, chipSubGroups } =
    processLotFiltersToGql(lookupExpanded);
  const lookupSubGroups = [
    ...lookupGroupsToLotSubGroups(andGroups, LogicalOperator.and),
    ...lookupGroupsToLotSubGroups(orGroups, LogicalOperator.or),
  ];

  const filter: FilterGroupOfLotAnimalEbvFilteredFilterOptionInput = {
    filters: baseFilters,
    groups: [
      {
        filters: processedFilters,
        logicalOperator: commonFilterOperator,
        groups: lookupSubGroups.length > 0 ? lookupSubGroups : undefined,
      },
      ...chipSubGroups,
    ],
    logicalOperator: LogicalOperator.and,
  };

  return [filter];
}
